/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useState, useEffect, useContext } from 'react'
import { useServiceContext } from '../../root'
import { RcmTableDataContext } from '../rcm-table/RcmTableDataContext'
import persistService from '../../services/persist'
import Events from '../../lib/Events'

const DEFAULT_PERSISTENCE_KEY = 'filterTrigger'

interface FilterTriggerProps {
  noPersist?: boolean
  persistenceKey?: string
  onToggle?: (isCollapsed: boolean) => void
}

export const FilterTrigger: React.FC<FilterTriggerProps> = ({ noPersist = false, persistenceKey, onToggle }) => {
  const context = useServiceContext()
  // Use useContext directly to avoid the error when outside RcmTableDataWrapper
  const tableData = useContext(RcmTableDataContext)
  const {
    service: {
      filter: { translate }
    }
  } = context

  const finalPersistenceKey = persistenceKey || DEFAULT_PERSISTENCE_KEY

  // Initialize from table context if available, otherwise from persistence
  const [isCollapsed, setIsCollapsed] = useState(() => {
    if (tableData?.isFilterCollapsed !== undefined) {
      return tableData.isFilterCollapsed
    }
    const settings = persistService.getBasic<{ collapsed: boolean }>(finalPersistenceKey)
    return settings?.collapsed ?? true
  })

  // Sync with table context on mount
  useEffect(() => {
    if (tableData?.setIsFilterCollapsed) {
      tableData.setIsFilterCollapsed(isCollapsed)
    }
  }, [])

  const handleToggle = () => {
    const newIsCollapsed = !isCollapsed

    setIsCollapsed(newIsCollapsed)

    // Update table context if available
    if (tableData?.setIsFilterCollapsed) {
      tableData.setIsFilterCollapsed(newIsCollapsed)
    }

    // Dispatch FILTER_TOGGLE event to event bus (CRITICAL: TableFilter listens for this!)
    const event = Events.FILTER_TOGGLE({ collapsed: newIsCollapsed })
    context.bus.dispatchEvent(new CustomEvent(event.type, { detail: event.payload }))

    // Notify parent if callback provided
    if (onToggle) {
      onToggle(newIsCollapsed)
    }

    // Persist if allowed
    if (!noPersist) {
      persistService.setBasic(finalPersistenceKey, { collapsed: newIsCollapsed })
    }
  }

  return (
    <a
      className="btn-link filterby-expander filterby-expander-react"
      id="main-filter-btn"
      onClick={handleToggle}
      style={{ cursor: 'pointer' }}
    >
      <span>{translate('FILTERS_FILTER_BY')}</span>
      <i className={`filterby-expander-caret-react fa ${isCollapsed ? 'fa-caret-down fa-lg' : 'fa-caret-up fa-lg'}`} />
    </a>
  )
}

export default FilterTrigger
